import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { WindowComponent } from '@ibc-shared/components/window/window.component';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule, TranslateModule, WindowComponent, RouterLink],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  protected isLoading = signal(false);
  protected errorMessage = signal<string | null>(null);
  protected showPassword = signal(false);

  form = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required, Validators.minLength(8)]),
  });

  ngOnInit(): void {
    this.form.valueChanges.subscribe(() => {
      if (this.errorMessage()) {
        this.errorMessage.set(null);
      }
    });
  }

  protected hasError(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    return !!(control && control.errors && control.touched);
  }

  protected getErrorMessage(fieldName: string): string {
    const control = this.form.get(fieldName);
    if (!control || !control.errors || !control.touched) return '';

    const errors = control.errors;

    if (errors['required']) return 'features.login.errors.requiredField';

    if (fieldName === 'email' && errors['email']) {
      return 'features.login.errors.invalidEmail';
    }

    return '';
  }

  protected onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const { email, password } = this.form.value;

    this.authService.login(email!, password!).subscribe({
      next: res => {
        if (res?.data?.auth) {
          this.router.navigate(['/']);
        } else {
          this.errorMessage.set(res.error?.message || 'features.login.errors.invalidCredentials');
        }
        this.isLoading.set(false);
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 401 || err.status === 404 || err.status === 400) {
          this.errorMessage.set('features.login.errors.wrongEmailPassword');
        } else if (err.status === 0) {
          this.errorMessage.set('features.login.errors.serverError');
        } else if (err.status === 403) {
          this.errorMessage.set('features.login.errors.accountNotVerified');
        } else {
          this.errorMessage.set(err.error?.message || 'features.login.errors.unexpectedError');
        }
        this.form.get('password')?.patchValue('', { emitEvent: false });
        this.isLoading.set(false);
      },
    });
  }
}
